import React, { useState } from "react"
import { useDispatch } from "react-redux"
import { ConfigContainer } from "components"
import { Container, Title, Text, Tabular } from "components/Form"
import { integrationSheets } from "state_management"
import useT from "utils/hooks/useT"

function getSheetValue(sheet, attr) {
  if (sheet[attr] !== undefined && sheet[attr] !== null) {
    return sheet[attr]
  }
  const camelAttr = attr.replace(/_([a-z])/g, (_, chr) => chr.toUpperCase())
  if (sheet[camelAttr] !== undefined && sheet[camelAttr] !== null) {
    return sheet[camelAttr]
  }
  return ""
}

// Configuration setup for integration sheets.
// TODO: Remove the reliance on the sheet's "__typename"
export default function IntegrationSheetConfig({ sheet, onSubmit, onClose }) {
  // This config works differently from most, it generates the Text inputs dynamically!
  const dispatch = useDispatch()
  const { t } = useT("common")
  const sheetFormat =
    sheet.__typename === "ActionUploadConfig"
      ? [
          { attr: "sheetName", title: t("sheetNameLabel"), required: true },
          { attr: "projectName", title: t("projectName"), required: true },
          { attr: "projectDescription", title: t("projectDescription"), required: true },
          { attr: "projectDeadline", title: t("projectDeadline"), required: true },
          { attr: "projectPriority", title: t("projectPriority"), required: false, default: "2" },
          { attr: "actionName", title: t("actionName"), required: true },
          { attr: "actionDescription", title: t("actionDescription"), required: true },
          { attr: "actionEarliestStart", title: t("actionEarliestStart"), required: true },
          { attr: "actionDeadline", title: t("actionDeadline"), required: true },
          { attr: "actionDuration", title: t("actionDuration"), required: true },
          { attr: "actionCompetenceGroup", title: t("actionCompetenceGroup"), required: true },
          { attr: "teamName", title: t("teamName"), required: false },
        ]
      : sheet.__typename === "SampleObservationUploadConfig"
      ? [
          { attr: "sheetName", title: t("sheetNameLabel"), required: true },
          { attr: "sampleName", title: t("sampleName"), required: true },
          { attr: "analysisName", title: t("analysisName"), required: true },
          { attr: "arrival", title: t("latestArrival"), required: true },
          { attr: "deadline", title: t("deadline"), required: true },
          { attr: "observationId", title: t("sampleId"), required: false, default: "1" },
          { attr: "priority", title: t("priority"), required: false, default: "2" },
          { attr: "status", title: t("status"), required: false, default: "ARRIVED" },
          { attr: "exclusive", title: t("exclusive"), required: false, default: "false" },
          { attr: "numberOfObservations", title: t("numberOfObservations"), required: false, default: "1" },
          { attr: "productName", title: t("productName"), required: false},
          { attr: "customer_name", title: t("customerName"), required: false },
          { attr: "teamName", title: t("teamName"), required: false },
        ]
      : sheet.__typename === "LimsUploadConfig"
      ? [
          { attr: "sheetName", title: t("sheetNameLabel"), required: true },
          { attr: "receivedDate", title: t("sampleReceivedDate"), required: true },
          { attr: "sampleBatchNumber", title: t("sampleBatchNumber"), required: true },
          { attr: "analysisName", title: t("sampleAnalysisName"), required: true },
          { attr: "observationId", title: t("sampleId"), required: false, default: "1" },
          { attr: "productDescription", title: t("sampleProductDescription"), required: false },
          { attr: "status", title: t("sampleStatus"), required: false, default: "ARRIVED" },
          { attr: "materialNumber", title: t("sampleMaterialNumber"), required: false },
          { attr: "customer_name", title: t("customerName"), required: false },
        ]
      : sheet.__typename === "SapUploadConfig"
      ? [
          { attr: "sheetName", title: t("sheetNameLabel"), required: true },
          { attr: "sapReleaseDate", title: t("projectReleaseDate"), required: true },
          { attr: "sapBatchNumber", title: t("projectBatchNumber"), required: true },
          { attr: "sapMaterialNumber", title: t("projectMaterialNumber"), required: true },
          { attr: "sapMaterialDescription", title: t("projectMaterialDescription"), required: true },
          { attr: "sapSupplierNumber", title: t("projectSupplierNumber"), required: false },
          { attr: "sapSupplierName", title: t("projectSupplierName"), required: false },
        ]
      : [
          { attr: "sheetName", title: t("sheetNameLabel"), required: true },
          { attr: "templateName", title: t("templateName"), required: true },
          { attr: "projectName", title: t("projectName"), required: true },
          { attr: "projectDescription", title: t("projectDescription"), required: false },
          { attr: "projectEarliestStart", title: t("projectEarliestStart"), required: true },
          { attr: "projectDeadline", title: t("projectDeadline"), required: false },
          { attr: "projectPriority", title: t("projectPriority"), required: false },
          { attr: "recurrenceAmount", title: t("noOfRecurrences"), required: false, dependsOn: "recurrenceInterval" },
          { attr: "recurrenceInterval", title: t("daysBetweenRecurrences"), required: false, dependsOn: "recurrenceAmount" },
          { attr: "teamName", title: t("teamName"), required: false },
        ]

  const [data, setData] = useState(() => {
    const rtn = {}
    sheetFormat.forEach((format) => {
      rtn[format.attr] = getSheetValue(sheet, format.attr)
    })
    return rtn
  })

  return (
    <ConfigContainer
      title={t("editSheet", { name: sheet.sheetName })}
      cancelHandler={onClose}
      submitHandler={() => {

        // When submitting, the input paramaters have their leading and trailing whitespaces trimmed.
        const dataTrimmed = data
        for(let key in dataTrimmed) {
          dataTrimmed[key] = dataTrimmed[key].trim()
        }
        setData(dataTrimmed)

        if (sheet.id) {
          if (sheet.__typename === "ActionUploadConfig") {
            dispatch(integrationSheets.editActionSheet(sheet.id, data)).then(onClose)
          } else if (sheet.__typename === "ProjectTemplateUploadConfig") {
            dispatch(integrationSheets.editProjectTemplateSheet(sheet.id, data)).then(onClose)
          } else if (sheet.__typename === "SampleObservationUploadConfig") {
            dispatch(integrationSheets.editSampleSheet(sheet.id, data)).then(onClose)
          } else if (sheet.__typename === "LimsUploadConfig"){
            dispatch(integrationSheets.editLimsAnalysisSheet(sheet.id, data)).then(onClose)
          } else {
            dispatch(integrationSheets.editSapAnalysisSheet(sheet.id, data)).then(onClose)
          }
        } else {
          if (sheet.__typename === "ActionUploadConfig") {
            dispatch(integrationSheets.createActionSheet(data)).then(onClose)
          } else if (sheet.__typename === "ProjectTemplateUploadConfig") {
            dispatch(integrationSheets.createProjectTemplateSheet(data)).then(onClose)
          } else if (sheet.__typename === "SampleObservationUploadConfig") {
            dispatch(integrationSheets.createSampleSheet(data)).then(onClose)
          } else if (sheet.__typename === "LimsUploadConfig"){
            dispatch(integrationSheets.createLimsAnalysisSheet(data)).then(onClose)
          } else {
            dispatch(integrationSheets.createSapAnalysisSheet(data)).then(onClose)
          }
        }
      }}
    >
      <Container>
        <Title>{t("type")}</Title>
        <Text
          disabled
          value={
            sheet.__typename === "ActionUploadConfig"
              ? t("actionsAndProjects")
              : sheet.__typename === "ProjectTemplateUploadConfig"
              ? t("projectTemplate")
              : sheet.__typename === "LimsUploadConfig"
              ? t("sampleImport")
              : sheet.__typename === "SapUploadConfig"
              ? t("projectImport")
              : t("sampleObservation")
          }
        />
      </Container>
      <Container key={sheetFormat[0].attr}>
        <Title value={t("sheetName")}>{t("sheetNameLabel")}</Title>
        <Text
          required={sheetFormat[0].required}
          value={data[sheetFormat[0].attr]}
          onChange={(value) => setData({ ...data, [sheetFormat[0].attr]: value })}
        />
      </Container>
      <Tabular>
        <thead>
          <tr>
            <td>
              <h6>{sheet.__typename === "LimsUploadConfig" ? t("sampleImportVariable") : sheet.__typename === "SapUploadConfig" ? t("projectImportVariable") : t("plannertechVariable")}</h6>
            </td>
            <td>
              <h6>{t("spreadsheetColumnName")}</h6>
            </td>
          </tr>
        </thead>
        <tbody>
          {sheetFormat.slice(1, sheetFormat.length).map((format) => (
            <tr key={format.attr}>
              <td>
                {format.title}
                {format.attr === "recurrenceInterval" ? ` ${t("mandatoryIfRecurrenceUsed")}` : (!format.required && ` ${format.default ? t("leaveEmptyIfNotUsedDefault", { defaultValue: format.default }) : t("leaveEmptyIfNotUsed")}`)}
              </td>
              <td>
                <Text
                  required={format.dependsOn ? (data[format.dependsOn].trim() !== "") : format.required}
                  value={data[format.attr]}
                  onChange={(value) => setData({ ...data, [format.attr]: value })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Tabular>
    </ConfigContainer>
  )
}
